import { Deduction, DeductionTypeCategoryKey, RemittanceItem } from "@/core/entities/deduction";
import { IDeductionColumns } from "@/core/repositories/deduction/IDeduction.repository";
import { TableColumnType, TableSortInfoColumnType } from "@ir/ui";

export const getBlankDeductions = (count: number): Deduction[] => {
  return Array.from(
    { length: count },
    () =>
      new Deduction({
        id: 0,
        glCodeId: 0,
        poNumber: "",
        purchaserId: 0,
        description: "",
        totalAmount: 0,
        amount: 0,
        checkNumber: "",
        checkDate: "",
        claimNumber: "",
        invoiceDate: "",
        period: "",
        glCode: "",
        purchaserKey: "",
        purchaser: "",
        deductionDate: "",
        deductionTypeId: 0,
        deductionTypeKey: "",
        deductionType: "",
        deductionNumber: "",
        deductionSubTypeId: 0,
        deductionSubTypeKey: "",
        deductionSubType: "",
        deductionSubTypeHelpText: "",
        remittanceItemId: 0,
        distributionCenter: "",
        distributionCenterCode: "",
        priorityId: 0,
        priorityKey: "",
        priorityName: "",
        validityId: 0,
        validityKey: "",
        validityName: "",
        rcaDescription: "",
        rcaIds: [],
        analystUserId: 0,
        analystUserFullName: "",
        analystUserAvatar: "",
        isAnalystDeleted: false,
        approverUserId: 0,
        approverUserFullName: "",
        approverUserAvatar: "",
        isApproverDeleted: false,
        retailerName: "",
        retailerKey: "",
        reasonCode: "",
        stages: [],
        status: {
          statusId: 0,
          statusKey: "",
          statusName: "",
          isCurrent: false,
        },
        documents: [],
        isDisputed: false,
        canDispute: false,
        isManuallyUploaded: false,
        isIncompleteDeduction: false,
        latestApprovalComments: [],
        deductionTypeCategory: {
          deductionTypeCategoryId: 0,
          deductionTypeCategoryKey: DeductionTypeCategoryKey.NON_TRADE,
          deductionTypeCategoryName: "",
        },
        controls: [],
        isPredictedPriority: false,
        feeBucket: null,
        feeBucketId: null,
        feeBucketLabel: null,
        splitDisplay: null,
      })
  );
};

export const isAllSelectedById = <T extends { id: number }>(selectedItems: T[], allItems: T[]): boolean => {
  return allItems.every(item => selectedItems.some(selectedItem => selectedItem.id === item.id));
};

export const isIndeterminateById = <T extends { id: number }>(selectedItems: T[], allItems: T[]): boolean => {
  return (
    allItems.some(item => selectedItems.some(selectedItem => selectedItem.id === item.id)) &&
    !isAllSelectedById(selectedItems, allItems)
  );
};

export const parseSearchKeywords = (searchValue?: string | null): string[] => {
  return searchValue?.split(",").filter(keyword => keyword.trim() !== "") ?? [];
};

export const getSortParamsFromTableSort = (
  sortedData: TableSortInfoColumnType[]
): { sortColumn: string | null; sortOrder: TableSortInfoColumnType["order"] | null } => {
  if (!sortedData || sortedData.length === 0) {
    return {
      sortColumn: null,
      sortOrder: null,
    };
  }

  return {
    sortColumn: sortedData[0].sortColumnKey as string,
    sortOrder: sortedData[0].order,
  };
};

type BuildColumnsFromConfigOptions = {
  pinnedColumnKeys?: {
    prepend?: string[];
    append?: string[];
  };
  insertedColumnKeys?: {
    key: string;
    index: number;
  }[];
};

export const buildColumnsFromConfig = <T extends object>(
  columns: IDeductionColumns[],
  tableColumns: TableColumnType<T>[],
  options?: BuildColumnsFromConfigOptions
): TableColumnType<T>[] => {
  const updatedColumns: TableColumnType<T>[] = [];

  for (const column of columns) {
    const currentColumnProps = tableColumns.find(col => col.key === column.key);
    if (currentColumnProps) {
      updatedColumns.push({
        ...currentColumnProps,
        title: column.title ?? currentColumnProps.title,
        dataIndex: column.key as keyof T,
        key: column.key,
        cellRender: currentColumnProps.cellRender,
      });
    }
  }

  const prependKeys = options?.pinnedColumnKeys?.prepend ?? [];
  for (const key of prependKeys) {
    const column = tableColumns.find(col => col.key === key);
    if (column) {
      updatedColumns.unshift(column);
    }
  }

  const insertions = options?.insertedColumnKeys ?? [];
  for (const insertion of insertions) {
    const column = tableColumns.find(col => col.key === insertion.key);
    if (column) {
      updatedColumns.splice(insertion.index, 0, column);
    }
  }

  const appendKeys = options?.pinnedColumnKeys?.append ?? [];
  for (const key of appendKeys) {
    const column = tableColumns.find(col => col.key === key);
    if (column) {
      updatedColumns.push(column);
    }
  }

  return updatedColumns;
};

type ConfirmDialogOptions = {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
};

export const confirmDiscardUnsavedChanges = async ({
  hasAnyUnsavedChanges,
  confirm,
  setHasAnyUnsavedChanges,
}: {
  hasAnyUnsavedChanges: boolean;
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
  setHasAnyUnsavedChanges: (value: boolean) => void;
}): Promise<boolean> => {
  if (!hasAnyUnsavedChanges) {
    return true;
  }

  const confirmResult = await confirm({
    title: "Unsaved Changes",
    message: "You have unsaved updates. Leave without saving?",
    confirmText: "Discard",
    cancelText: "Go Back",
  });

  if (confirmResult) {
    setHasAnyUnsavedChanges(false);
  }

  return confirmResult;
};

export const buildDeductionDrawerInfo = (record: Deduction) => {
  return {
    remittanceItemId: record.remittanceItemId,
    remittanceItem: {
      isDeduction: true,
    } as RemittanceItem,
    checkNumber: record.checkNumber,
    invoiceNumber: record.claimNumber,
    isRemittanceItemsPage: false,
    purchaserKey: record.purchaserKey,
    isDeductionIncomplete: record.isIncompleteDeduction ?? false,
    isManuallyUploaded: record.isManuallyUploaded ?? false,
    nextPrevEnabled: false,
  };
};

export const openDeductionDrawerFromRow = async ({
  record,
  showUnsavedConfirmation,
  setCurrentExpandedDeductionId,
  setDrawerInfo,
  setDrawerOpen,
}: {
  record: Deduction;
  showUnsavedConfirmation: () => Promise<boolean>;
  setCurrentExpandedDeductionId: (id: number) => void;
  setDrawerInfo: (drawerInfo: ReturnType<typeof buildDeductionDrawerInfo>) => void;
  setDrawerOpen: (isOpen: boolean) => void;
}): Promise<void> => {
  const confirmResult = await showUnsavedConfirmation();
  if (!confirmResult) {
    return;
  }

  setCurrentExpandedDeductionId(record.id);
  setDrawerInfo(buildDeductionDrawerInfo(record));
  setDrawerOpen(true);
};

export const handleSelectAllRows = <T extends { id: number }>({
  checked,
  selectedItems,
  visibleItems,
  setSelectedItems,
  clearSingleSelection,
}: {
  checked: boolean;
  selectedItems: T[];
  visibleItems: T[];
  setSelectedItems: (items: T[]) => void;
  clearSingleSelection?: () => void;
}): void => {
  if (checked) {
    setSelectedItems([...selectedItems, ...visibleItems]);
    clearSingleSelection?.();
    return;
  }

  setSelectedItems(selectedItems.filter(item => !visibleItems.some(visibleItem => visibleItem.id === item.id)));
};

export const handleSelectRowCheckbox = <T>({
  checked,
  record,
  selectedItems,
  setSelectedItems,
  clearSingleSelection,
  isSameItem,
}: {
  checked: boolean;
  record: T;
  selectedItems: T[];
  setSelectedItems: (items: T[]) => void;
  clearSingleSelection?: () => void;
  isSameItem: (left: T, right: T) => boolean;
}): void => {
  if (checked) {
    setSelectedItems([...selectedItems, record]);
  } else {
    setSelectedItems(selectedItems.filter(item => !isSameItem(item, record)));
  }
  clearSingleSelection?.();
};
